import {controller, target, targets} from '@github/catalyst'
import {isGtBreakpoint, isTouchEnabled} from '../../modules/marketing/helpers'

@controller
export class TestimonialsCarouselElement extends HTMLElement {
  @target declare container: HTMLElement
  @target declare nextButton: HTMLButtonElement
  @target declare prevButton: HTMLButtonElement
  @target declare currentDisplay: HTMLSpanElement
  @target declare currentAnnounce: HTMLSpanElement
  @target declare carousel: HTMLDivElement
  @targets declare carouselItems: HTMLElement[]
  touchStart = 0
  touchEnd = 0
  activeIndex = 0
  canMoveNext = false
  canMovePrev = false

  connectedCallback() {
    if (isTouchEnabled()) {
      this.container.classList.add('testimonials-touch')
    }
    // Adjust carousel on window resize
    new ResizeObserver(() => {
      this.setCurrentItem(this.activeIndex)
      this.setCarouselDirections()
    }).observe(this.carousel as Element)
  }

  setCarouselDirections = () => {
    this.canMovePrev = this.activeIndex !== 0
    // In mobile we only show one item at a time instead of two:
    this.canMoveNext = isGtBreakpoint('sm') // Hide "next" button if last two items are active
      ? this.activeIndex !== this.carouselItems.length - 2 && this.activeIndex !== this.carouselItems.length - 1
      : this.activeIndex !== this.carouselItems.length - 1 // Hide "next" button if last item is active

    if (this.canMovePrev) {
      this.prevButton.disabled = false
    } else {
      this.prevButton.disabled = true
    }

    if (this.canMoveNext) {
      this.nextButton.disabled = false
    } else {
      this.nextButton.disabled = true
    }
  }

  setCurrentItem = (index: number) => {
    if (this.carouselItems.length === 0) {
      return
    }
    let transformAmount = 0
    this.carouselItems[this.activeIndex]!.toggleAttribute('data-testimonial-active')
    this.carouselItems[index]!.toggleAttribute('data-testimonial-active')
    this.activeIndex = index

    if (index > 0) {
      // In conjunction with app/assets/stylesheets/marketing/customer-stories.scss
      const constant = isGtBreakpoint('sm') ? 48 : 80 // width of each carousel item
      const gap = isGtBreakpoint('sm') ? 4 : 8 // gap between each carousel item
      transformAmount = this.activeIndex * constant + gap
    }

    this.carousel.setAttribute('style', `transform: translateX(-${transformAmount}%);`)
    this.currentDisplay.textContent = `${this.activeIndex + 1} / ${this.carouselItems.length}`
    this.currentAnnounce.textContent = `${this.activeIndex + 1} of ${this.carouselItems.length}`
  }

  moveToPrev = (ev: Event) => {
    ev.preventDefault()
    this.setCurrentItem(this.activeIndex - 1)
    this.setCarouselDirections()
  }

  moveToNext = (ev: Event) => {
    ev.preventDefault()
    this.setCurrentItem(this.activeIndex + 1)
    this.setCarouselDirections()
  }

  onTouchStart = (ev: TouchEvent) => {
    this.touchStart = ev.changedTouches[0]!.screenX
  }

  onTouchEnd = (ev: TouchEvent) => {
    const minSwipe = 20
    this.touchEnd = ev.changedTouches[0]!.screenX

    if (this.touchEnd < this.touchStart - minSwipe && this.canMoveNext) {
      this.moveToNext(ev)
    }

    if (this.touchEnd > this.touchStart + minSwipe && this.canMovePrev) {
      this.moveToPrev(ev)
    }
  }
}
